import type { IFlags } from '@flagsmith/flagsmith';

/**
 * Every Flagsmith flag this app reads, in one place.
 *
 * Keep this list in alphabetical order so a flag is quick to find and two branches adding
 * a flag do not both append to the same last line and conflict. A test enforces it.
 *
 * To add a flag: create it in the Flagsmith dashboard, add its name here in order, then
 * read it with `useFeatureFlag('tx_my_flag')`.
 *
 * To remove a flag: delete it from this list. TypeScript then fails on every call site
 * that still reads it, so nothing is left behind by accident.
 *
 * The list is the single source of truth for the flag names, which is why keys are typed
 * instead of being free-form strings: a typo in a flag name would otherwise read as
 * "feature off" and silently hide working code.
 */
export const FEATURE_FLAG_KEYS = [
  'tx_analytics_email_table',
  'tx_analytics_sms_table',
  'tx_ao_notifications',
  'tx_ca_custom_module',
  'tx_content_dismiss',
  'tx_context-data-queue-v2',
  'tx_custom_analytics_enhancements',
  'tx_delete_context_files',
  'tx_demo',
  'tx_design_and_code_content',
  'tx_email_sms_custom_analytics_enabled',
  'tx_enable_experience_settings_in_actions_header',
  'tx_enable_touchpoint_editor',
  'tx_es1_link_visible',
  'tx_filters',
  'tx_flow_canvas_controls',
  'tx_flow_layered_layout',
  'tx_flow_multiple_contents',
  'tx_flow_v2',
  'tx_folders',
  'tx_journey_template_visible_to_flybits',
  'tx_labels',
  'tx_live_analytics',
  'tx_maker_checker',
  'tx_merchant_portal',
  'tx_show_template_library_banner',
  'tx_standalone_slideouts',
  'tx_template_library_modal',
  'tx_theme_settings',
  'tx_zone_module_label_to_content',
] as const;

export type FeatureFlagKey = (typeof FEATURE_FLAG_KEYS)[number];

/**
 * Every flag starts off until Flagsmith says otherwise.
 *
 * Flagsmith overwrites these the moment real flags arrive, so in normal operation they
 * are never read. They matter when Flagsmith is unreachable: the SDK then resolves flags
 * from this map instead of having no flag data at all, so a new feature stays hidden
 * rather than half-rendering, and the app itself still loads.
 */
export const DEFAULT_FLAGS: IFlags<FeatureFlagKey> = Object.fromEntries(
  FEATURE_FLAG_KEYS.map((key) => [key, { enabled: false, value: null }]),
) as IFlags<FeatureFlagKey>;
